import { Box, Button, Card, CardContent, CardHeader, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import "../../../shared/styles/style.css";
import { workshopCardStyle, workshopCardHeaderStyle } from "../../../shared/styles/cardStyles";
import { chooseButtonTurquoiseStyle } from "../../../shared/styles/buttonStyles";
import { useLocation, useNavigate } from "react-router-dom";
import Rating from "../../../shared/components/Rating";
import { useTranslation } from "react-i18next";
import ExtraInfo from "./workshop_extras/ExtraInfo";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShowFullTextOnHover from "../../../shared/components/ShowFullTextOnHover";
import NumberLocation from "./workshop_extras/NumberLocation";
import Reviews from "./Reviews";
import { useDispatch, useSelector } from "react-redux";
import { emptyWorkshopForMap, setWorkshopForMap, workshopForMap } from "../../../app/store/workshopSlice";
import { useGetRatingsQuery } from "../../../app/store/api/workshopApi";
import orderByContext from "../../../app/providers/orderBy/orderByContext";
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Holidays from "date-holidays";
import dayjs from "dayjs";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

export default function WorkshopListCard({ workshop }) {
  const { t } = useTranslation();
  const { state: orderByState } = React.useContext(orderByContext);
  const { isDesktop, isMobile } = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const {data:ratingsData} = useGetRatingsQuery(
    { workshopId: workshop?.id },
    { skip: !workshop?.id }
  );
  const {ratings} = ratingsData || {};
  const dispatch = useDispatch();
  const storedSelectedWorkshopForMap = useSelector(workshopForMap);
  const queueSize = workshop?.queueSize || 3;
  const holidays = new Holidays('FI');
  const today = dayjs().startOf('day');
  const munhuoltoPriceEstimate = "300";

  useEffect(() => {
    setShow(false);
    setLoading(false);
  }, [location]);

  const handleCardClick = () => {
    if (JSON.stringify(storedSelectedWorkshopForMap) === JSON.stringify(workshop)) {
      dispatch(emptyWorkshopForMap());
    } else {
      dispatch(setWorkshopForMap(workshop));
    }
  };

  const isWeekend = (date) => {
      const day = date.getDay();
      return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };

  // Check if date is a holiday
  const isHoliday = useCallback((date) => {
      const year = dayjs(date).year();

      const publicHolidays = holidays.getHolidays(year).filter(holiday => holiday.type === 'public');

      return publicHolidays.some(
          holiday => dayjs(holiday.date).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD')
      );
  }, [holidays]);

  const findNextAvailableDate = useCallback((days) => {
    let currentDate = today.clone();
    let workingDays = 0;

    while (workingDays < days) {
      currentDate = currentDate.add(1, 'day');

      const date = currentDate.toDate();

      if (isWeekend(date) || isHoliday(date)) {
        continue;
      }

      workingDays++;
    }

    return dayjs((currentDate).toDate()).format('D.M.');
  }, [today, isHoliday]);

  const nextAvailableDate = useMemo(() => findNextAvailableDate(queueSize), [findNextAvailableDate, queueSize]);

  return (
    <Grid
      className='workshop-grid-item'
      size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
      sx={{
        padding: 0,
        marginTop: isDesktop ? "20px" : "8px !important",
      }}
    >
      {/* Revommended & possible campaign info */}

      {workshop?.favoredByMunhuolto && orderByState.orderBy === "recommended" && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              minHeight: isMobile ? "28px" : "36px",
              width: "100%",
              px: 1,
              borderRadius: "10px 10px 0 0",
              backgroundColor: "#EAFBFC",
              color: "white",
            }}
          >
            <StarBorderIcon sx={{ fontSize: "1rem", mr: 0.5, color: "#ffc400" }} />
            <Typography
              component="span"
              sx={{ fontSize: isMobile ? "0.7rem" : "0.9rem", fontWeight: 700, color: "#003C59" }}
            >
              {t("workshop.recommended")}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              px: 1.5,
              py: isMobile ? 0.75 : 1,
              borderRadius: "0 10px 0 0",
              backgroundColor: "#EAFBFC",
              color: "#003C59",
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? "0.6rem" : "0.8rem",
                lineHeight: 1.35,
                textAlign: "left",
                color: "#003C59"
              }}
            >
              {t("workshop.recommendationBasis")}
            </Typography>
          </Box>
        </>
      )}
      
      <Card
        className='workshop-card'
        sx={{
          ...workshopCardStyle,
          padding: isDesktop ? undefined : "12px 16px",
        }}
        onClick={handleCardClick}
      >
        {!isDesktop ? (
          // Compact mobile/tablet layout
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <NumberLocation orderNr={workshop.orderNr} />
              <Typography variant='subtitle1' sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>
                {workshop.name}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, paddingRight: "8px" }}>
                <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: "block" }}>
                  {workshop.address}
                </Typography>
                {workshop.distance && (
                  <Typography variant='caption' sx={{ color: "text.secondary", display: "block" }}>
                    {t("workshop.workshopDistance")} {workshop.distance} km
                  </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center", marginTop: "4px", gap: "8px", flexWrap: "wrap" }}>
                  <Rating rating={ratings} />
                  <Reviews workshopId={workshop.id} />
                </Box>
                <Box sx={{ marginTop: 1 }}>
                  {nextAvailableDate ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <CalendarTodayIcon sx={{ fontSize: "0.75rem" }} />
                      <Box sx={{ fontSize: "0.65rem" }}>
                        {t("workshop.nextAvailableDate")} : {nextAvailableDate}
                      </Box>
                    </Box>
                  ) : null}
                  {munhuoltoPriceEstimate ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <LocalOfferIcon sx={{ fontSize: "0.75rem" }} />
                      <Box sx={{ fontSize: "0.65rem" }}>
                        {t("workshop.munhuoltoPriceEstimate")} : {munhuoltoPriceEstimate} &euro;
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ marginTop: "4px", display: "flex", justifyContent: "flex-start" }} onClick={(e) => e.stopPropagation()}>
                <Button
                  onClick={() => setShow(!show)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        fontSize: "1rem",
                        transform: show ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                  }
                  sx={{
                    textTransform: "none",
                    textDecoration: "underline",
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    p: 0,
                    minWidth: 0,
                    "&:hover": {
                      background: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {t("workshop.showMore")}
                </Button>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center", paddingLeft: "8px" }} onClick={(e) => e.stopPropagation()}>
                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    ...chooseButtonTurquoiseStyle,
                    px: 2,
                    py: 0.5,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    minWidth: "80px",
                    borderRadius: "20px",
                  }}
                  onClick={() => {
                    navigate(`/workshops/${workshop.id}?step=1`);
                  }}
                >
                  {t("actions.choose")}
                </Button>
              </Box>
            </Box>

            {show && (
              <Box sx={{ marginTop: "8px" }} onClick={(e) => e.stopPropagation()}>
                <ExtraInfo workshop={workshop} show={show} />
              </Box>
            )}
          </Box>
        ) : (
          // Desktop layout (Original)
          <>
            <CardHeader
              sx={workshopCardHeaderStyle}
              avatar={<NumberLocation orderNr={workshop.orderNr} />}
              title={
                <Typography variant='h6' component='div' className='workshop-card-header'>
                  {workshop.name}
                </Typography>
              }
              subheader={
                <>
                  <Box>{workshop.address}</Box>
                  {workshop.distance && (
                    <Box>
                      {t("workshop.workshopDistance")} {workshop.distance} km
                    </Box>
                  )}
                </>
              }
            />
            <CardContent>
              <Grid container spacing={2} className='workshop-card-content'>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                  <Rating rating={ratings} />
                  <Reviews workshopId={workshop.id} />
                  <Box sx={{ marginTop: 1 }}>
                    {nextAvailableDate ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <CalendarTodayIcon sx={{ fontSize: "0.75rem" }} />
                        <Box sx={{ fontSize: "0.65rem" }}>
                          {t("workshop.nextAvailableDate")} : {nextAvailableDate}
                        </Box>
                      </Box>
                    ) : null}
                    {munhuoltoPriceEstimate ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <LocalOfferIcon sx={{ fontSize: "0.75rem" }} />
                        <Box sx={{ fontSize: "0.65rem" }}>
                          {t("workshop.munhuoltoPriceEstimate")} : {munhuoltoPriceEstimate} &euro;
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                  {workshop.extraDescription && workshop.extraDescription.trim() !== "" && <ShowFullTextOnHover text={workshop.extraDescription} maxLength={60} />}
                </Grid>
              </Grid>
            </CardContent>

            {!loading && (
              <Grid container spacing={1} sx={{ p: 2, pt: 0 }}>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                  <Button
                    onClick={() => setShow(!show)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: show ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease-in-out",
                        }}
                      />
                    }
                    sx={{
                      textTransform: "none",
                      textDecoration: "underline",
                      fontSize: "0.9rem",
                      color: "black",
                      "&:hover": {
                        background: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("workshop.showMore")}
                  </Button>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                  <Button
                    variant='contained'
                    size='medium'
                    sx={{ ...chooseButtonTurquoiseStyle, width: "200px", height: "40px", fontSize: "0.9rem", textTransform: "none" }}
                    onClick={() => {
                      navigate(`/workshops/${workshop.id}?step=1`);
                    }}
                  >
                    {t("actions.choose")}
                  </Button>
                </Grid>
              </Grid>
            )}

            {show && (
              <CardContent>
                <ExtraInfo workshop={workshop} show={show} />
              </CardContent>
            )}
          </>
        )}
      </Card>
    </Grid>
  );
}

// xs={12} sm={6} md={6} lg={6}
